// Utilitaires géométriques sans allocation dans les boucles chaudes.
import * as THREE from 'three';
import type { IndexArray } from './format';

/**
 * Normales lissées par sommet "logique" (sommet de base ou sommet .obj d'un proxy), pondérées par
 * l'aire : les sommets dupliqués aux coutures UV partagent ainsi la même normale (pas de couture visible).
 * tris : triangles en indices logiques. out : nVerts*3.
 */
export function computeNormals(pos: Float32Array, tris: Uint32Array, out: Float32Array) {
  accumulateFaceNormals(pos, tris, out);
  normalizeAll(out);
}

export function normalizeAll(v: Float32Array) {
  for (let i = 0, n = v.length; i < n; i += 3) {
    const l = Math.sqrt(v[i] * v[i] + v[i + 1] * v[i + 1] + v[i + 2] * v[i + 2]);
    if (l > 1e-12) {
      v[i] /= l; v[i + 1] /= l; v[i + 2] /= l;
    } else {
      v[i] = 0; v[i + 1] = 1; v[i + 2] = 0;
    }
  }
}

/** out[r] = src[map[r]] (vecteurs 3D) : sommets logiques -> sommets de rendu. */
export function expand3(src: Float32Array, map: IndexArray, out: Float32Array) {
  for (let r = 0, n = map.length; r < n; r++) {
    const s = map[r] * 3, o = r * 3;
    out[o] = src[s]; out[o + 1] = src[s + 1]; out[o + 2] = src[s + 2];
  }
}

/** Convertit des triangles en indices de rendu en indices logiques. */
export function logicalTris(index: IndexArray, map: IndexArray): Uint32Array {
  const out = new Uint32Array(index.length);
  for (let i = 0; i < index.length; i++) out[i] = map[index[i]];
  return out;
}

/** Adjacence sommet -> triangles (CSR). */
export interface Adjacency { start: Uint32Array; tris: Uint32Array }

export function vertexTriangleAdjacency(tris: Uint32Array, nVerts: number): Adjacency {
  const count = new Uint32Array(nVerts + 1);
  for (let i = 0; i < tris.length; i++) count[tris[i] + 1]++;
  for (let v = 0; v < nVerts; v++) count[v + 1] += count[v];
  const fill = count.slice(0, nVerts);
  const out = new Uint32Array(tris.length);
  for (let i = 0, t = 0; i < tris.length; i += 3, t++) {
    out[fill[tris[i]]++] = t;
    out[fill[tris[i + 1]]++] = t;
    out[fill[tris[i + 2]]++] = t;
  }
  return { start: count, tris: out };
}

/**
 * Deltas de normales des morphs creux, par recalcul local : seuls les triangles adjacents aux sommets
 * déplacés sont recalculés (une fois chacun). Pour un sommet touché :
 *   n' = normalize(accBase + Σ_tri (n_tri(morphé) − n_tri(base)))   (accumulations non normalisées)
 * et le delta vaut n' − n(base).
 */
export class MorphNormalBuilder {
  private readonly accBase: Float32Array;     // normales non normalisées de la forme de base
  private readonly acc: Float32Array;
  private readonly triMark: Uint8Array;
  private readonly vertMark: Uint8Array;
  private readonly triList: Uint32Array;
  readonly touched: Uint32Array;
  touchedCount = 0;

  constructor(
    private readonly pos: Float32Array, private readonly baseNormals: Float32Array,
    private readonly tris: Uint32Array, private readonly adj: Adjacency,
  ) {
    const nv = pos.length / 3;
    this.accBase = new Float32Array(nv * 3);
    this.acc = new Float32Array(nv * 3);
    this.triMark = new Uint8Array(tris.length / 3);
    this.vertMark = new Uint8Array(nv);
    this.triList = new Uint32Array(tris.length / 3);
    this.touched = new Uint32Array(nv);
    accumulateFaceNormals(pos, tris, this.accBase);
  }

  /** disp : déplacements denses du morph (nVerts*3) ; moved : sommets déplacés ; écrit outDelta (logique). */
  build(disp: Float32Array, moved: ArrayLike<number>, outDelta: Float32Array) {
    const { pos, tris, adj, acc, accBase, baseNormals, triMark, vertMark, triList, touched } = this;
    const start = adj.start, at = adj.tris;
    let nt = 0, nv = 0;
    for (let i = 0; i < moved.length; i++) {
      const v = moved[i];
      for (let k = start[v], e = start[v + 1]; k < e; k++) {
        const t = at[k];
        if (!triMark[t]) { triMark[t] = 1; triList[nt++] = t; }
      }
    }
    for (let i = 0; i < nt; i++) {
      const t = triList[i] * 3;
      const a = tris[t] * 3, b = tris[t + 1] * 3, c = tris[t + 2] * 3;
      // normale de base
      let e1x = pos[b] - pos[a], e1y = pos[b + 1] - pos[a + 1], e1z = pos[b + 2] - pos[a + 2];
      let e2x = pos[c] - pos[a], e2y = pos[c + 1] - pos[a + 1], e2z = pos[c + 2] - pos[a + 2];
      const ox = e1y * e2z - e1z * e2y, oy = e1z * e2x - e1x * e2z, oz = e1x * e2y - e1y * e2x;
      // normale morphée
      e1x += disp[b] - disp[a]; e1y += disp[b + 1] - disp[a + 1]; e1z += disp[b + 2] - disp[a + 2];
      e2x += disp[c] - disp[a]; e2y += disp[c + 1] - disp[a + 1]; e2z += disp[c + 2] - disp[a + 2];
      const dx = e1y * e2z - e1z * e2y - ox, dy = e1z * e2x - e1x * e2z - oy, dz = e1x * e2y - e1y * e2x - oz;
      for (let j = 0; j < 3; j++) {
        const v = tris[t + j], o = v * 3;
        acc[o] += dx; acc[o + 1] += dy; acc[o + 2] += dz;
        if (!vertMark[v]) { vertMark[v] = 1; touched[nv++] = v; }
      }
      triMark[triList[i]] = 0;
    }
    for (let i = 0; i < nv; i++) {
      const o = touched[i] * 3;
      const x = accBase[o] + acc[o], y = accBase[o + 1] + acc[o + 1], z = accBase[o + 2] + acc[o + 2];
      const l = Math.sqrt(x * x + y * y + z * z) || 1;
      outDelta[o] = x / l - baseNormals[o];
      outDelta[o + 1] = y / l - baseNormals[o + 1];
      outDelta[o + 2] = z / l - baseNormals[o + 2];
      acc[o] = acc[o + 1] = acc[o + 2] = 0;
      vertMark[touched[i]] = 0;
    }
    this.touchedCount = nv;
  }
}

/** Accumule (sans normaliser) les normales de faces pondérées par l'aire. */
export function accumulateFaceNormals(pos: Float32Array, tris: Uint32Array, out: Float32Array) {
  out.fill(0);
  for (let t = 0, n = tris.length; t < n; t += 3) {
    const a = tris[t] * 3, b = tris[t + 1] * 3, c = tris[t + 2] * 3;
    const e1x = pos[b] - pos[a], e1y = pos[b + 1] - pos[a + 1], e1z = pos[b + 2] - pos[a + 2];
    const e2x = pos[c] - pos[a], e2y = pos[c + 1] - pos[a + 1], e2z = pos[c + 2] - pos[a + 2];
    const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    out[a] += nx; out[a + 1] += ny; out[a + 2] += nz;
    out[b] += nx; out[b + 1] += ny; out[b + 2] += nz;
    out[c] += nx; out[c + 1] += ny; out[c + 2] += nz;
  }
}

/**
 * Boîte et sphère englobantes calculées sur les positions de repos uniquement (BufferGeometry.computeBounding*
 * parcourt aussi tous les morph targets : trop lent avec ~65 morphs). Agrandies d'une marge pour la pose.
 */
export function setBounds(g: THREE.BufferGeometry, pos: Float32Array, margin = 0.15) {
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let i = 0; i < pos.length; i += 3) {
    const x = pos[i], y = pos[i + 1], z = pos[i + 2];
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
    if (z < z0) z0 = z; if (z > z1) z1 = z;
  }
  if (!(x0 <= x1)) return;
  const box = (g.boundingBox ??= new THREE.Box3());
  box.min.set(x0 - margin, y0 - margin, z0 - margin);
  box.max.set(x1 + margin, y1 + margin, z1 + margin);
  const s = (g.boundingSphere ??= new THREE.Sphere());
  box.getCenter(s.center);
  s.radius = box.min.distanceTo(box.max) / 2;
}
